import { sortAscByMaxParticipants } from 'services/utils'
import {
  Hint,
  QuantityHint,
  TimeBeforeAfterHint,
  TimeBeforeHint,
} from 'types/common'
import {
  APIWithQuantityHint,
  APIWithTimeBeforeAfterHint,
  APIWithTimeBeforeHint,
  WithQuantityHint,
  WithTimeBeforeAfterHint,
  WithTimeBeforeHint,
} from './types'

const DEFAULT_HINT_MAX_PARTICIPANTS = 1000000
const DEFAULT_HINT_HOURS_BEFORE = 0
const DEFAULT_HINT_HOURS_AFTER = 0

export const DEFAULT_QUANTITY_HINT: QuantityHint = {
  maxParticipants: DEFAULT_HINT_MAX_PARTICIPANTS,
  quantityMultiplier: 100,
}

export const DEFAULT_TIME_BEFORE_HINT: TimeBeforeHint = {
  maxParticipants: DEFAULT_HINT_MAX_PARTICIPANTS,
  hoursBefore: DEFAULT_HINT_HOURS_BEFORE,
}

export const DEFAULT_TIME_BEFORE_AFTER_HINT: TimeBeforeAfterHint = {
  maxParticipants: DEFAULT_HINT_MAX_PARTICIPANTS,
  hoursBefore: DEFAULT_HINT_HOURS_BEFORE,
  hoursAfter: DEFAULT_HINT_HOURS_AFTER,
}

export const computeQuantityByMaxParticipants = (
  quantityHint: QuantityHint[],
  maxParticipants?: number,
  quantityMultiplier?: number,
  guestsNumber?: number
) => {
  // if there is a value not filled we return '/'
  if (!maxParticipants || !guestsNumber || !quantityMultiplier) return '/'
  const clonedQuantityHint = [...quantityHint]

  // if in the hints array there is no element with the corresponding maxParticipants that
  // means that this is the default hint  that need to be pushed like the last element
  if (!clonedQuantityHint.find((e) => e.maxParticipants === maxParticipants))
    clonedQuantityHint.push({ maxParticipants, quantityMultiplier })

  // we need to sort the hints because we need to find the lower limit to know if we are in
  // the correct range
  clonedQuantityHint.sort(sortAscByMaxParticipants)

  // we search in the sorted array of hints to find the index
  const index = clonedQuantityHint.findIndex(
    (e) => e.maxParticipants === maxParticipants
  )

  // then we can find the min participants searching if present the previous element in the sorted array
  const minParticipants =
    index > 0 ? clonedQuantityHint[index - 1].maxParticipants : 0
  // if the range is correct we return the computed quantity
  return guestsNumber <= maxParticipants && guestsNumber > minParticipants
    ? Math.ceil((guestsNumber * quantityMultiplier) / 100)
    : '/'
}

const writeHint = <T extends Hint>(
  isEnabled: boolean,
  defaultHint: T,
  hints: T[]
) => {
  if (isEnabled) {
    hints.push(defaultHint)
    return hints
  } else return []
}

/**
 * @returns the quantity hint array if show quantity is true else empty array
 */
export const writeQuantityHint = ({
  defaultQuantityHint,
  quantityHint,
  showQuantityHint,
}: WithQuantityHint) =>
  writeHint(showQuantityHint, defaultQuantityHint, quantityHint)

/**
 * @returns the before hint array if show time before hint is true else empty array
 */
export const writeTimeBeforeHint = ({
  timeBeforeHint,
  defaultTimeBeforeHint,
  showTimeBeforeHint,
}: WithTimeBeforeHint) =>
  writeHint(showTimeBeforeHint, defaultTimeBeforeHint, timeBeforeHint)

/**
 * @returns the time before after hint array if show time before after hint is true else empty array
 */
export const writeTimeBeforeAfterHint = ({
  timeBeforeAfterHint,
  defaultTimeBeforeAfterHint,
  showTimeBeforeAfterHint,
}: WithTimeBeforeAfterHint) =>
  writeHint(
    showTimeBeforeAfterHint,
    defaultTimeBeforeAfterHint,
    timeBeforeAfterHint
  )

/**
 * @returns the quantity hint, the default and the toggle value
 */
export const parseQuantityHint = <T extends APIWithQuantityHint>({
  quantityHint,
}: T): WithQuantityHint => {
  quantityHint.sort(sortAscByMaxParticipants)
  return {
    showQuantityHint: !!quantityHint.length,
    defaultQuantityHint: quantityHint.pop() ?? DEFAULT_QUANTITY_HINT,
    quantityHint,
    guestsNumberMock: 0,
  }
}

/**
 * @returns the time before hint, the default and the toggle value
 */
export const parseTimeBeforeHint = <T extends APIWithTimeBeforeHint>({
  timeBeforeHint,
}: T): WithTimeBeforeHint => {
  timeBeforeHint.sort(sortAscByMaxParticipants)
  return {
    showTimeBeforeHint: !!timeBeforeHint.length,
    defaultTimeBeforeHint: timeBeforeHint.pop() ?? DEFAULT_TIME_BEFORE_HINT,
    timeBeforeHint,
  }
}

/**
 * @returns the time before after hint, the default and the toggle value
 */
export const parseTimeBeforeAfterHint = <T extends APIWithTimeBeforeAfterHint>({
  timeBeforeAfterHint,
}: T): WithTimeBeforeAfterHint => {
  timeBeforeAfterHint.sort(sortAscByMaxParticipants)
  return {
    showTimeBeforeAfterHint: !!timeBeforeAfterHint.length,
    defaultTimeBeforeAfterHint:
      timeBeforeAfterHint.pop() ?? DEFAULT_TIME_BEFORE_AFTER_HINT,
    timeBeforeAfterHint,
  }
}
